import React, { useState, useEffect } from 'react';
import config from '../config';

const { API_URL } = config;

const PrerequisitesCheck = ({ userEmail, programName, onPrerequisitesMet, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [prerequisites, setPrerequisites] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkPrerequisites = async () => {
      try {
        // Demo mode - simulate prerequisites check
        const token = localStorage.getItem('token');
        if (token === 'demo-token-for-local-testing') {
          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Demo: Prerequisites not met
          setPrerequisites({
            can_start_exam: false,
            prerequisites: [
              {
                type: 'aws_cloud_practitioner',
                display_name: 'AWS Certified Cloud Practitioner',
                required: true,
                status: 'pending',
                message: 'Please contact your administrator to upload and verify your certificate'
              }
            ]
          });
          setLoading(false);
          return;
        }

        const response = await fetch(
          `${API_URL}/enrollments/prerequisites?user_email=${encodeURIComponent(userEmail)}&program_name=${encodeURIComponent(programName)}`,
          {
            headers: {
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            }
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        
        // Validate API response structure
        if (!data || typeof data !== 'object') {
          throw new Error('Invalid API response: expected object');
        }
        
        // Handle both API response formats:
        // Format 1 (detailed): { can_start_exam: bool, prerequisites: [...] } - array of prerequisite objects
        // Format 2 (simplified): { prerequisites: {program_name: {met, verified}} } - object keyed by program
        let normalizedData = data;
        
        // Check if this is the simplified format (object with program keys instead of array)
        if (data.prerequisites && typeof data.prerequisites === 'object' && !Array.isArray(data.prerequisites)) {
          // Convert simplified format to detailed format
          const programPrereq = data.prerequisites[programName];
          if (programPrereq) {
            // Explicit boolean checks for safety
            const isMet = programPrereq.met === true;
            const isVerified = programPrereq.verified === true;
            
            normalizedData = {
              can_start_exam: isMet && isVerified,
              prerequisites: [
                {
                  type: 'aws_cloud_practitioner',
                  display_name: 'AWS Certified Cloud Practitioner',
                  required: true,
                  status: isVerified ? 'verified' : (isMet ? 'uploaded' : 'pending'),
                  message: isVerified 
                    ? 'Certificate verified' 
                    : (isMet 
                      ? 'Certificate uploaded and pending verification by administrator'
                      : 'Please contact your administrator to upload and verify your certificate')
                }
              ]
            };
          } else {
            // No prerequisites data for this program
            normalizedData = {
              can_start_exam: false,
              prerequisites: [
                {
                  type: 'aws_cloud_practitioner',
                  display_name: 'AWS Certified Cloud Practitioner',
                  required: true,
                  status: 'pending',
                  message: 'Please contact your administrator to upload and verify your certificate'
                }
              ]
            };
          }
        } else if (data.prerequisites && Array.isArray(data.prerequisites)) {
          // Format 1 (detailed) - validate required fields
          if (typeof data.can_start_exam !== 'boolean') {
            console.warn('Missing or invalid can_start_exam field in API response, defaulting to false');
            normalizedData = {
              ...data,
              can_start_exam: false
            };
          }
          
          // Validate prerequisites array structure
          const validPrereqs = data.prerequisites.every(prereq => 
            prereq && 
            typeof prereq === 'object' &&
            prereq.type &&
            prereq.display_name &&
            prereq.status
          );
          
          if (!validPrereqs) {
            throw new Error('Invalid prerequisites structure in API response');
          }
        } else {
          // Unexpected format
          throw new Error('Invalid API response format: prerequisites must be an array or object');
        }
        
        setPrerequisites(normalizedData);

        // If prerequisites are met, notify parent
        if (normalizedData.can_start_exam === true) {
          onPrerequisitesMet();
        }
      } catch (err) {
        console.error('Error checking prerequisites:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    checkPrerequisites();
  }, [userEmail, programName, onPrerequisitesMet]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div className="loading-spinner" style={{
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #007bff',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p>Checking prerequisites...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        maxWidth: '600px',
        margin: '50px auto',
        padding: '30px',
        background: '#f8d7da',
        border: '1px solid #f5c6cb',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <h2 style={{color: '#721c24', marginBottom: '20px'}}>⚠️ Error</h2>
        <p style={{color: '#721c24', marginBottom: '20px'}}>
          Unable to check prerequisites: {error}
        </p>
        <button 
          onClick={onBack}
          style={{
            padding: '10px 20px',
            background: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  if (!prerequisites || prerequisites.can_start_exam) {
    // Prerequisites met, parent component will handle loading exam
    return null;
  }

  // Prerequisites not met - show message
  return (
    <div style={{
      maxWidth: '700px',
      margin: '50px auto',
      padding: '40px',
      background: 'white',
      border: '2px solid #ffc107',
      borderRadius: '10px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    }}>
      <div style={{textAlign: 'center', marginBottom: '30px'}}>
        <div style={{
          fontSize: '60px',
          marginBottom: '20px'
        }}>
          🔒
        </div>
        <h2 style={{color: '#856404', marginBottom: '10px'}}>
          Prerequisites Required
        </h2>
        <p style={{color: '#666', fontSize: '16px'}}>
          The following prerequisites must be provided before you can continue with the <strong>{programName}</strong> assessment:
        </p>
      </div>

      <div style={{
        background: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '30px'
      }}>
        {prerequisites.prerequisites.map((prereq, index) => (
          <div key={index} style={{
            marginBottom: index < prerequisites.prerequisites.length - 1 ? '20px' : '0',
            paddingBottom: index < prerequisites.prerequisites.length - 1 ? '20px' : '0',
            borderBottom: index < prerequisites.prerequisites.length - 1 ? '1px solid #ffc107' : 'none'
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px'}}>
              <span style={{
                fontSize: '24px',
                color: prereq.status === 'verified' ? '#28a745' : '#dc3545'
              }}>
                {prereq.status === 'verified' ? '✓' : '✗'}
              </span>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                color: '#333'
              }}>
                {prereq.display_name}
              </h3>
              {prereq.required && (
                <span style={{
                  background: '#dc3545',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  REQUIRED
                </span>
              )}
            </div>
            <p style={{
              margin: '0 0 0 34px',
              color: '#666',
              fontSize: '14px'
            }}>
              {prereq.message}
            </p>
          </div>
        ))}
      </div>

      <div style={{
        background: '#e7f3ff',
        border: '1px solid #b3d9ff',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '30px'
      }}>
        <h4 style={{margin: '0 0 10px 0', color: '#004085'}}>
          📧 Next Steps
        </h4>
        <ol style={{margin: '0', paddingLeft: '20px', color: '#004085'}}>
          <li style={{marginBottom: '8px'}}>
            Contact your program administrator
          </li>
          <li style={{marginBottom: '8px'}}>
            Provide proof of your AWS Cloud Practitioner certification
          </li>
          <li style={{marginBottom: '8px'}}>
            Wait for verification (you'll receive an email notification)
          </li>
          <li>
            Return here to start your exam once verified
          </li>
        </ol>
      </div>

      <div style={{textAlign: 'center'}}>
        <button 
          onClick={onBack}
          style={{
            padding: '12px 30px',
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          ← Back to Programs
        </button>
      </div>
    </div>
  );
};

export default PrerequisitesCheck;
